import { collection, doc, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { db } from "@/firebase/app";
import { useCollection, useDoc } from "@/hooks/firestoreHooks";
import { parseCompetitionInstance, parseCompetitionTemplate } from "@/models/competition";
import { eventDisplay, type LeagueCompetitionEventDisplay } from "@/models/eventDisplay";
import { parseMemberSummary, type LeagueMemberSummary } from "@/models/memberSummary";

// Shared data source for the competition-scoped GPS/course screens.

export interface CompetitionDisplayState {
  display: LeagueCompetitionEventDisplay | null;
  members: LeagueMemberSummary[];
  loading: boolean;
  error: string | null;
}

export function useCompetitionDisplay(instanceId: string | undefined): CompetitionDisplayState {
  const instance = useDoc(instanceId ? doc(db, "leagueCompetitionInstances", instanceId) : null, parseCompetitionInstance);
  const template = useDoc(
    instance.data ? doc(db, "leagueCompetitionTemplates", instance.data.templateID) : null,
    parseCompetitionTemplate,
  );
  const leagueID = instance.data?.leagueID ?? null;
  const membersQuery = leagueID ? query(collection(db, "leagueMemberSummaries"), where("leagueID", "==", leagueID)) : null;
  const members = useCollection(membersQuery, parseMemberSummary, `members:${leagueID}`);

  return useMemo(
    () => ({
      display: instance.data ? eventDisplay(instance.data, template.data ?? undefined) : null,
      members: members.data,
      loading: instance.loading || template.loading,
      error: instance.error ?? template.error,
    }),
    [instance.data, instance.loading, instance.error, template.data, template.loading, members.data],
  );
}
